import React, { useState } from 'react';
import useLang from '../../i18n';
import { ChevronLeft, ChevronRight, Search, Loader2, AlertTriangle, X, Check } from 'lucide-react';
import clsx from 'clsx';
import { fmtNum } from '../../utils/dateUtils';

// ── نمایش دوگانه ارز — هر مبلغ فقط با سمبول واقعی خودش، بدون تبدیل ─
// افغانی برجسته/بولد است؛ دالر (اگر واقعاً تراکنشی به دالر وجود داشته باشد) کوچک‌تر نمایش داده می‌شود.
// اگر یکی از دو ارز هیچ تراکنشی نداشته باشد (صفر)، اصلاً نمایش داده نمی‌شود.
export const MoneyDual = ({ afn = 0, usd = 0, size = 'lg', className = '' }) => {
  const sizes = {
    lg: { afn: 'text-2xl', usd: 'text-sm' },
    md: { afn: 'text-lg',  usd: 'text-xs' },
    sm: { afn: 'text-sm',  usd: 'text-[11px]' },
  };
  const s = sizes[size] || sizes.lg;
  const hasAfn = Math.round(afn || 0) !== 0;
  const hasUsd = Math.round(usd || 0) !== 0;
  if (!hasAfn && !hasUsd) {
    return <span className={`${s.afn} font-bold text-gray-900 ${className}`}>؋0</span>;
  }
  return (
    <div className={`flex items-baseline gap-2 flex-wrap ${className}`}>
      {hasAfn && <span className={`${s.afn} font-bold text-gray-900`}>؋{fmtNum(Math.round(afn || 0))}</span>}
      {hasUsd && <span className={`${s.usd} font-medium text-gray-500`}>${fmtNum(Math.round(usd || 0))}</span>}
    </div>
  );
};

// ── Button ────────────────────────────────────────────────
export const Button = ({ children, variant='default', size='md', loading, icon, className, ...props }) => {
  const base = 'inline-flex items-center gap-2 font-semibold rounded-xl border transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    default: 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300',
    primary: 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700',
    danger:  'bg-red-600 text-white border-red-600 hover:bg-red-700',
    ghost:   'bg-transparent text-gray-600 border-transparent hover:bg-gray-100',
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base',
  };
  return (
    <button className={clsx(base, variants[variant], sizes[size], className)} disabled={loading} {...props}>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
      {children}
    </button>
  );
};

// ── Input ─────────────────────────────────────────────────
export const Input = React.forwardRef(({ label, error, hint, className, ...props }, ref) => (
  <div>
    {label && <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>}
    <input ref={ref}
      className={clsx(
        'w-full px-4 py-2.5 text-sm border rounded-xl bg-white text-gray-900 outline-none transition-all',
        'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
        error && 'border-red-400 focus:border-red-400 focus:ring-red-100',
        className
      )} {...props} />
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    {hint && !error && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
));

// ── Select ────────────────────────────────────────────────
export const Select = React.forwardRef(({ label, error, options=[], placeholder, className, ...props }, ref) => (
  <div>
    {label && <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>}
    <select ref={ref}
      className={clsx(
        'w-full px-4 py-2.5 text-sm border rounded-xl bg-white text-gray-900 outline-none transition-all',
        'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
        error && 'border-red-400',
        className
      )} {...props}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
));

// ── Textarea ──────────────────────────────────────────────
export const Textarea = React.forwardRef(({ label, error, rows=3, className, ...props }, ref) => (
  <div>
    {label && <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>}
    <textarea ref={ref} rows={rows}
      className={clsx(
        'w-full px-4 py-2.5 text-sm border rounded-xl bg-white text-gray-900 outline-none transition-all resize-none',
        'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
        error && 'border-red-400',
        className
      )} {...props} />
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
));

// ── CurrencyInput ─────────────────────────────────────────
export const CurrencyInput = React.forwardRef(({ label, error, currency='AFN', ...props }, ref) => (
  <div>
    {label && <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>}
    <div className="relative">
      <input ref={ref} type="number" min="0" step="any"
        className={clsx(
          'w-full pl-16 pr-4 py-2.5 text-sm border rounded-xl bg-white outline-none transition-all',
          'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
          error && 'border-red-400'
        )} {...props} />
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
        {currency === 'AFN' ? '؋' : '$'}
      </span>
    </div>
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
));

// ── Card ──────────────────────────────────────────────────
export const Card = ({ children, className, ...props }) => (
  <div className={clsx('bg-white border border-gray-200 rounded-2xl shadow-sm', className)} {...props}>
    {children}
  </div>
);

// ── Badge ─────────────────────────────────────────────────
const BADGE = {
  green: 'bg-green-100 text-green-700',
  red:   'bg-red-100 text-red-600',
  amber: 'bg-amber-100 text-amber-700',
  blue:  'bg-blue-100 text-blue-700',
  gray:  'bg-gray-100 text-gray-600',
};
export const Badge = ({ label, color='gray', dot }) => (
  <span className={clsx('inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold', BADGE[color])}>
    {dot && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
    {label}
  </span>
);

const STATUS_MAP = {
  approved:'green', active:'green', paid:'green', confirmed:'green', settled:'green',
  pending:'amber', partial:'amber', overdue:'amber', draft:'amber',
  cancelled:'red', inactive:'red', rejected:'red',
  transfer:'gray', cash:'blue', credit:'amber',
};
export const StatusBadge = ({ status }) => (
  <Badge label={status} color={STATUS_MAP[status]||'gray'} dot />
);

// ── SearchBox ─────────────────────────────────────────────
export const SearchBox = ({ value, onChange, placeholder='' }) => (
  <div className="relative">
    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-56 pr-10 pl-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 outline-none focus:bg-white focus:border-blue-500 focus:w-72 transition-all" />
  </div>
);

// ── Pagination ────────────────────────────────────────────
export const Pagination = ({ pagination, onChange }) => {
  if (!pagination || pagination.totalPages <= 1) return null;
  const { page, totalPages } = pagination;
  const pages = Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1);
  return (
    <div className="flex items-center justify-center gap-1.5 pt-4 pb-2">
      <button onClick={() => onChange(page-1)} disabled={!pagination.hasPrev}
        className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center disabled:opacity-40 hover:bg-gray-50 transition-colors">
        <ChevronRight className="w-4 h-4" />
      </button>
      {pages.map(p => (
        <button key={p} onClick={() => onChange(p)}
          className={clsx('w-9 h-9 rounded-xl text-sm font-semibold transition-all',
            p===page ? 'bg-blue-600 text-white' : 'border border-gray-200 hover:bg-gray-50 text-gray-600')}>
          {p}
        </button>
      ))}
      <button onClick={() => onChange(page+1)} disabled={!pagination.hasNext}
        className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center disabled:opacity-40 hover:bg-gray-50 transition-colors">
        <ChevronLeft className="w-4 h-4" />
      </button>
    </div>
  );
};

// ── Modal ─────────────────────────────────────────────────
export const Modal = ({ isOpen, onClose, title, children, size='md' }) => {
  if (!isOpen) return null;
  const sizes = { sm:'max-w-sm', md:'max-w-lg', lg:'max-w-2xl', xl:'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={clsx('bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto', sizes[size])} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// ── ConfirmDeleteModal ────────────────────────────────────
export const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm, loading, itemName }) => {
  const { t } = useLang();
  const [reason, setReason] = useState('');
  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('deleteConfirm')} size="sm">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-red-500" />
        </div>
        <div>
          <p className="text-sm font-bold text-gray-900">{t('confirm')}</p>
          <p className="text-sm text-gray-500 mt-0.5">{itemName||'این رکورد'} حذف خواهد شد.</p>
        </div>
      </div>
      <Textarea label="دلیل حذف *" rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="دلیل حذف را بنویسید..." />
      <div className="flex gap-2 justify-end mt-4">
        <Button onClick={onClose}>انصراف</Button>
        <Button variant="danger" loading={loading} disabled={!reason.trim()} onClick={() => onConfirm(reason)}>
          <AlertTriangle className="w-4 h-4" /> حذف
        </Button>
      </div>
    </Modal>
  );
};

// ── PageHeader ────────────────────────────────────────────
export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex items-start justify-between mb-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </div>
);

// ── Breadcrumb ────────────────────────────────────────────
export const Breadcrumb = ({ items }) => (
  <div className="flex items-center gap-1.5 text-sm text-gray-400 mb-4">
    {items.map((item, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span className="text-gray-300">›</span>}
        <span className={i===items.length-1 ? 'text-gray-600 font-medium' : 'hover:text-blue-600 cursor-pointer'}>{item}</span>
      </React.Fragment>
    ))}
  </div>
);

// ── FormRow ───────────────────────────────────────────────
export const FormRow = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);

// ── LoadingSpinner ────────────────────────────────────────
export const LoadingSpinner = ({ full }) => (
  <div className={clsx('flex items-center justify-center', full && 'min-h-[200px]')}>
    <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
  </div>
);

// ── EmptyState ────────────────────────────────────────────
export const EmptyState = ({ icon:Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-14 text-center">
    {Icon && <Icon className="w-14 h-14 text-gray-200 mb-3" />}
    <p className="text-gray-500 font-semibold text-sm">{title||t('noData')}</p>
    {description && <p className="text-gray-400 text-xs mt-1">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

// ── DataTable ─────────────────────────────────────────────
export const DataTable = ({ columns, data, loading, emptyTitle, emptyIcon, actions, pagination, onPageChange }) => (
  <div>
    {/* Desktop Table */}
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} className="bg-gray-50 text-gray-500 font-bold px-4 py-3 text-right text-xs border-b border-gray-200 whitespace-nowrap">
                {col.label}
              </th>
            ))}
            {actions && <th className="bg-gray-50 text-gray-500 font-bold px-4 py-3 text-right text-xs border-b border-gray-200">{'Actions / عملیات'}</th>}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length+(actions?1:0)} className="py-12"><LoadingSpinner full /></td></tr>
          ) : data.length===0 ? (
            <tr><td colSpan={columns.length+(actions?1:0)}>
              <EmptyState icon={emptyIcon} title={emptyTitle} />
            </td></tr>
          ) : data.map((row, i) => (
            <tr key={row._id||i} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
              {columns.map(col => (
                <td key={col.key} className="px-4 py-3.5 text-gray-700 text-sm">
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
              {actions && <td className="px-4 py-3.5">{actions(row)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Mobile Cards */}
    <div className="md:hidden">
      {loading ? (
        <div className="py-12"><LoadingSpinner full /></div>
      ) : data.length===0 ? (
        <EmptyState icon={emptyIcon} title={emptyTitle} />
      ) : data.map((row, i) => (
        <div key={row._id||i} className="border-b border-gray-100 px-4 py-4 hover:bg-gray-50/60">
          {/* First column as title */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex-1 min-w-0">
              {columns[0] && (
                <div className="text-sm font-bold text-gray-900">
                  {columns[0].render ? columns[0].render(row[columns[0].key], row) : row[columns[0].key]}
                </div>
              )}
              {columns[1] && (
                <div className="text-xs text-gray-400 mt-0.5">
                  {columns[1].render ? columns[1].render(row[columns[1].key], row) : row[columns[1].key]}
                </div>
              )}
            </div>
            {actions && (
              <div className="flex-shrink-0">{actions(row)}</div>
            )}
          </div>
          {/* Rest of columns as key-value pairs */}
          <div className="grid grid-cols-2 gap-2">
            {columns.slice(2).map(col => (
              <div key={col.key} className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-[10px] text-gray-400 mb-0.5">{col.label}</p>
                <div className="text-xs font-semibold text-gray-700">
                  {col.render ? col.render(row[col.key], row) : (row[col.key] || '—')}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    {pagination && <Pagination pagination={pagination} onChange={onPageChange} />}
  </div>
);
